import React, { createRef } from 'react';
import PropTypes from 'prop-types';
import AsyncSelect from 'react-select/lib/Async';
import AsyncCreatableSelect from 'react-select/lib/AsyncCreatable';
import { defaultTheme } from 'react-select';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import CancelIcon from '@material-ui/icons/Cancel';
import {
    Typography, TextField, MenuItem, Chip, Paper,
} from '@material-ui/core';
import { emphasize } from '@material-ui/core/styles/colorManipulator';
import suggestionsPropType from '../../../proptypes/suggestionsPropType';

const styles = theme => ({
    root: {
        flexGrow: 1,
    },
    input: {
        display: 'flex',
        padding: 0,
        height: 'auto',
    },
    valueContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        flex: 1,
        alignItems: 'center',
        overflow: 'hidden',
        padding: theme.spacing(1.35),
    },
    chip: {
        margin: `0px ${theme.spacing(0.25)}px ${theme.spacing(0.25)}px`,
    },
    chipFocused: {
        backgroundColor: emphasize(
            theme.palette.type === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
            0.08,
        ),
    },
    noOptionsMessage: {
        padding: theme.spacing(1, 2),
    },
    singleValue: {
        fontSize: 16,
    },
    placeholder: {
        position: 'absolute',
        left: theme.spacing(1.5),
        fontSize: 16,
    },
    paper: {
        position: 'absolute',
        zIndex: 100,
        marginTop: theme.spacing(1),
        left: 0,
        right: 0,
    },
    hidden: {
        display: 'none',
    },
    disabled: {
        opacity: 0.5,
    },
    divider: {
        height: theme.spacing(2),
    },
});

const NoOptionsMessage = (props) => {
    const {
        innerProps,
        selectProps,
    } = props;
    return (
        <Typography
            color="textSecondary"
            className={selectProps.classes.noOptionsMessage}
            {...innerProps}
        >
            {' '}
        </Typography>
    );
};
NoOptionsMessage.propTypes = {
    innerProps: PropTypes.objectOf(PropTypes.any),
    selectProps: PropTypes.objectOf(PropTypes.any).isRequired,
};
NoOptionsMessage.defaultProps = {
    innerProps: null,
};

const LoadingMessage = () => (
    <></>
);

const inputComponent = ({ inputRef, ...props }) => <div ref={inputRef} {...props} />;
inputComponent.propTypes = {
    inputRef: PropTypes.func.isRequired,
};


const Control = (props) => {
    const {
        selectProps,
        innerRef,
        children,
        innerProps,
    } = props;
    return (
        <TextField
            fullWidth
            variant="outlined"
            margin="dense"
            InputProps={{
                inputComponent,
                inputProps: {
                    className: selectProps.classes.input,
                    inputRef: innerRef,
                    children,
                    ...innerProps,
                },
            }}
            {...selectProps.textFieldProps}
        />
    );
};
Control.propTypes = {
    innerProps: PropTypes.objectOf(PropTypes.any).isRequired,
    innerRef: PropTypes.func.isRequired,
    children: PropTypes.arrayOf(PropTypes.node).isRequired,
    selectProps: PropTypes.objectOf(PropTypes.any).isRequired,
};

const Option = (props) => {
    const {
        innerRef,
        children,
        innerProps,
        isFocused,
        isSelected,
    } = props;
    return (
        <MenuItem
            buttonRef={innerRef}
            selected={isFocused}
            component="div"
            style={{
                fontWeight: isSelected ? 500 : 400,
            }}
            {...innerProps}
        >
            {children}
        </MenuItem>
    );
};
Option.propTypes = {
    innerProps: PropTypes.objectOf(PropTypes.any).isRequired,
    innerRef: PropTypes.func,
    isFocused: PropTypes.bool.isRequired,
    isSelected: PropTypes.bool.isRequired,
    children: PropTypes.string.isRequired,
};
Option.defaultProps = {
    innerRef: null,
};

const Placeholder = (props) => {
    const {
        selectProps,
        children,
        innerProps,
    } = props;
    return (
        <Typography
            color="textSecondary"
            className={selectProps.classes.placeholder}
            {...innerProps}
        >
            {children}
        </Typography>
    );
};
Placeholder.propTypes = {
    selectProps: PropTypes.objectOf(PropTypes.any).isRequired,
    innerProps: PropTypes.objectOf(PropTypes.any),
    children: PropTypes.string.isRequired,
};
Placeholder.defaultProps = {
    innerProps: null,
};

const ValueContainer = (props) => {
    const {
        selectProps,
        children,
    } = props;
    return <div className={selectProps.classes.valueContainer}>{children}</div>;
};
ValueContainer.propTypes = {
    selectProps: PropTypes.objectOf(PropTypes.any).isRequired,
    children: PropTypes.arrayOf(PropTypes.node).isRequired,
};

const MultiValue = (props) => {
    const {
        selectProps,
        children,
        isFocused,
        removeProps,
    } = props;
    return (
        <Chip
            tabIndex={-1}
            label={children}
            className={classNames(selectProps.classes.chip, {
                [selectProps.classes.chipFocused]: isFocused,
            })}
            onDelete={removeProps.onClick}
            deleteIcon={<CancelIcon {...removeProps} />}
        />
    );
};
MultiValue.propTypes = {
    selectProps: PropTypes.objectOf(PropTypes.any).isRequired,
    removeProps: PropTypes.objectOf(PropTypes.any).isRequired,
    isFocused: PropTypes.bool.isRequired,
    children: PropTypes.string.isRequired,
};

const Menu = (props) => {
    const {
        selectProps,
        children,
        options,
        innerProps,
    } = props;
    return (
        <Paper
            square
            className={classNames(
                selectProps.classes.paper,
                { [selectProps.classes.hidden]: !options || options.length === 0 },
            )}
            {...innerProps}
        >
            {children}
        </Paper>
    );
};
Menu.propTypes = {
    selectProps: PropTypes.objectOf(PropTypes.any).isRequired,
    innerProps: PropTypes.objectOf(PropTypes.any).isRequired,
    children: PropTypes.objectOf(PropTypes.any).isRequired,
    options: suggestionsPropType.isRequired,
};

const components = {
    Control,
    Menu,
    MultiValue,
    NoOptionsMessage,
    Option,
    Placeholder,
    ValueContainer,
    DropdownIndicator: null,
};


const MultiAutocomplete = ({
    classes,
    theme,
    selectedSuggestions,
    handleSelect,
    placeholder,
    handleGetSuggestions,
    handleSubmit,
    disabled,
    label,
    creatable,
}) => {
    const selectRef = createRef();

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !selectRef.current.select.state.menuIsOpen) {
            handleSubmit();
        }
    };

    const customTheme = {
        ...defaultTheme,
        colors: { ...defaultTheme.colors, neutral80: theme.palette.text.primary },
    };

    const selectProps = {
        classes,
        styles,
        textFieldProps: {
            label,
            InputLabelProps: {
                shrink: true,
            },
        },
        components,
        value: selectedSuggestions,
        onChange: handleSelect,
        onKeyDown: handleKeyDown,
        placeholder,
        loadOptions: handleGetSuggestions,
        theme: customTheme,
        loadingMessage: LoadingMessage,
        isDisabled: disabled,
        ref: selectRef,
        isMulti: true,
    };

    const creatableProps = {
        formatCreateLabel: input => `${input}`,
    };

    return (
        <div
            className={classNames(classes.root, {
                [classes.disabled]: disabled,
            })}
        >
            {creatable ? (
                <AsyncCreatableSelect
                    {...selectProps}
                    {...creatableProps}
                    allowCreateWhileLoading
                />
            ) : (
                <AsyncSelect
                    {...selectProps}
                />
            )}
        </div>
    );
};

MultiAutocomplete.propTypes = {
    classes: PropTypes.objectOf(PropTypes.any).isRequired,
    theme: PropTypes.objectOf(PropTypes.any).isRequired,
    placeholder: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    selectedSuggestions: suggestionsPropType.isRequired,
    handleSelect: PropTypes.func.isRequired,
    handleGetSuggestions: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    creatable: PropTypes.bool,
    disabled: PropTypes.bool,
};

MultiAutocomplete.defaultProps = {
    creatable: false,
    disabled: false,
};

export default withStyles(styles, { withTheme: true })(MultiAutocomplete);
